import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import AnimateHeight from "react-animate-height";

import SvgIcon from "components/Common/SvgIcon";

import "./styles.scss";

const BLOCK_NAME = "Banner";

/**
 * Renders a styled banner at the top of the Modal window.
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {unknown} props.icon
 * @param {unknown} props.intent
 * @param {boolean} props.isVisible
 * @param {Function} props.onClose
 * @returns {React.ReactNode}
 */
const Banner = ({
  children,
  icon,
  intent,
  isVisible,
  onClose
}) => {
  const isClosable = typeof onClose === "function";
  const containerClass = classNames(BLOCK_NAME, `${BLOCK_NAME}--${intent}`, {
    [`${BLOCK_NAME}--closable`]: isClosable
  });

  return (
    <AnimateHeight height={isVisible ? "auto" : 0}>
      <div className={containerClass}>
        <SvgIcon icon={icon} height={20} customClassName={`${BLOCK_NAME}__icon`} />

        {children}

        {isClosable && (
          <SvgIcon
            icon="Close"
            height={18}
            customClassName={`${BLOCK_NAME}__close`}
            onClick={onClose}
          />
        )}
      </div>
    </AnimateHeight>
  );
};

Banner.defaultProps = {
  icon: "Banana",
  onClose: null,
  intent: "default",
  isVisible: true
};

Banner.propTypes = {
  children: PropTypes.node.isRequired,

  /**
   * The icon to display on the left side
   */
  icon: PropTypes.string,

  /**
   * The intent of the banner, which changes the background color
   */
  intent: PropTypes.oneOf(["default", "positive", "negative"]),

  /**
   * The function to fire when the close button is clicked
   */
  onClose: PropTypes.func,

  /**
   * Whether or not this entire banner should be visible. Uses `react-animate-height` to do the
   * hiding/showing.
   */
  isVisible: PropTypes.bool
};

export default Banner;
